import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";

import { Feedback, useResource } from "@/components/workflow/shared";
import { siteEquipmentApi, type EquipmentPlace } from "@/lib/api/oprema";
import { procitajGreske } from "@/lib/greske";
import { Field, useEquipmentAccess } from "./shared";
import { EquipmentDetail } from "./EquipmentList";
import {
  EquipmentDraft,
  useEquipmentDraftState,
  type EquipmentDraftCache,
} from "./EquipmentDraft";
import { EquipmentPlaceCards } from "./EquipmentHome";
import { useEquipmentNavigation } from "./EquipmentNavigation";

export function EquipmentPlaces() {
  const access = useEquipmentAccess();
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const resource = useResource(
    "equipment-places",
    useCallback((signal) => siteEquipmentApi.overview(signal), []),
  );
  const navigation = useEquipmentNavigation();
  const editId = navigation.params.get("uredi");
  const editing =
    editId === "new"
      ? "new"
      : (resource.data?.mjesta.find((p) => p.id === editId) ?? null);
  const setEditing = (value: EquipmentPlace | "new" | null) =>
    value
      ? navigation.open({ uredi: value === "new" ? "new" : value.id })
      : navigation.close(["uredi"]);
  const deactivating =
    resource.data?.mjesta.find(
      (p) => p.id === navigation.params.get("ugasi"),
    ) ?? null;
  const setDeactivating = (value: EquipmentPlace | null) =>
    value ? navigation.open({ ugasi: value.id }) : navigation.close(["ugasi"]);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState("");
  return (
    <>
      <div className="equipment-row">
        <h2 className="text-xl font-bold">Mjesta</h2>
        {access.all && (
          <Button onClick={() => setEditing("new")}>Dodaj mjesto</Button>
        )}
      </div>
      <p>Gradilišta dolaze iz Gradilišta. Ovdje se dodaju Baze i Radione.</p>
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {message && <p role="status">{message}</p>}
      {resource.loading && <p role="status">Učitavanje mjesta…</p>}
      {resource.data &&
        ([false, true] as const).map((sites) => (
          <section className="equipment-place-section" key={String(sites)}>
            <h3 className="equipment-label">
              {sites
                ? "Gradilišta (dolaze iz izbornika Gradilišta)"
                : "Baze i radione"}
            </h3>
            {resource
              .data!.mjesta.filter((p) => (p.vrsta === "gradiliste") === sites)
              .map((place) => (
                <article key={place.id} aria-label={place.naziv}>
                  <EquipmentPlaceCards data={resource.data!} places={[place]} />
                  {place.vrsta !== "gradiliste" &&
                    access.all &&
                    place.aktivno && (
                      <div className="equipment-place-manage">
                        <Button
                          variant="outline"
                          onClick={() => setEditing(place)}
                        >
                          Preimenuj {place.naziv}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setError("");
                            setDeactivating(place);
                          }}
                        >
                          Ugasi {place.naziv}
                        </Button>
                      </div>
                    )}
                  {place.vrsta !== "gradiliste" &&
                    access.all &&
                    !place.aktivno && (
                      <Button
                        variant="outline"
                        disabled={pending}
                        onClick={() => {
                          setPending(true);
                          setError("");
                          void siteEquipmentApi
                            .patchPlace(place.id, { aktivno: true })
                            .then(() => {
                              setMessage("Mjesto ponovno uključeno.");
                              resource.reload();
                            })
                            .catch((cause) => {
                              const errors = procitajGreske(cause);
                              setError(
                                [
                                  ...errors.opcenito,
                                  ...Object.values(errors.polja),
                                ].join(" "),
                              );
                            })
                            .finally(() => setPending(false));
                        }}
                      >
                        Ponovno uključi
                      </Button>
                    )}
                </article>
              ))}
            {!resource.data!.mjesta.some(
              (p) => (p.vrsta === "gradiliste") === sites,
            ) && (
              <p>
                {sites
                  ? "Nema dostupnih gradilišta."
                  : "Još nema Baze ni Radione."}
              </p>
            )}
          </section>
        ))}
      {editing && access.all && (
        <EquipmentDraft
          key={editing === "new" ? "new" : editing.id}
          cache={drafts}
          name={editing === "new" ? "new" : editing.id}
        >
          <PlaceEditor
            key={editing === "new" ? "new" : editing.id}
            place={editing === "new" ? undefined : editing}
            places={resource.data?.mjesta ?? []}
            onClose={() => setEditing(null)}
            onSaved={() => {
              drafts.clear();
              setEditing(null);
              setMessage("Mjesto spremljeno.");
              resource.reload();
            }}
          />
        </EquipmentDraft>
      )}
      {deactivating && access.all && (
        <EquipmentDetail
          pending={pending}
          title={`Ugasi ${deactivating.naziv}`}
          onClose={() => {
            if (!pending) setDeactivating(null);
          }}
        >
          <p>
            Ugasiti mjesto {deactivating.naziv}? Mjesto mora biti prazno, bez
            otvorenih selidbi, najmova i potvrda.
          </p>
          <Feedback error={error} />
          <Button
            disabled={pending}
            onClick={() => {
              setPending(true);
              setError("");
              void siteEquipmentApi
                .patchPlace(deactivating.id, { aktivno: false })
                .then(() => {
                  setDeactivating(null);
                  setMessage("Mjesto deaktivirano.");
                  resource.reload();
                })
                .catch((cause) => {
                  const errors = procitajGreske(cause);
                  setError(
                    [...errors.opcenito, ...Object.values(errors.polja)].join(
                      " ",
                    ),
                  );
                })
                .finally(() => setPending(false));
            }}
          >
            {pending ? "Spremam…" : "Ugasi mjesto"}
          </Button>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => setDeactivating(null)}
          >
            Odustani
          </Button>
        </EquipmentDetail>
      )}
      {!deactivating && <Feedback error={error} />}
    </>
  );
}

function PlaceEditor({
  place,
  places,
  onClose,
  onSaved,
}: {
  place?: EquipmentPlace;
  places: EquipmentPlace[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useEquipmentDraftState("name", place?.naziv ?? "");
  const [kind, setKind] = useEquipmentDraftState<"baza" | "radiona">(
    "kind",
    "baza",
  );
  const [external, setExternal] = useEquipmentDraftState("external", false);
  const [supplier, setSupplier] = useEquipmentDraftState("supplier", "");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const duplicate = places.some(
    (other) =>
      other.id !== place?.id &&
      other.naziv.trim().toLocaleLowerCase("hr") ===
        name.trim().toLocaleLowerCase("hr"),
  );
  const suppliers = useResource(
    `equipment-suppliers:${external}`,
    useCallback(
      (signal) =>
        external ? siteEquipmentApi.suppliers(signal) : Promise.resolve([]),
      [external],
    ),
  );
  return (
    <EquipmentDetail
      pending={pending}
      title={place ? "Preimenuj mjesto" : "Dodaj mjesto"}
      onClose={() => {
        if (!pending) onClose();
      }}
    >
      <form
        className="equipment-form"
        aria-label={place ? "Preimenuj mjesto" : "Dodaj mjesto"}
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || (external && !supplier) || pending) return;
          setPending(true);
          setError("");
          void (
            place
              ? siteEquipmentApi.patchPlace(place.id, { naziv: name.trim() })
              : siteEquipmentApi.createPlace({
                  naziv: name.trim(),
                  vrsta: kind,
                  dobavljac_id: external ? supplier : null,
                })
          )
            .then(onSaved)
            .catch((cause) => {
              const errors = procitajGreske(cause);
              setError(
                [...errors.opcenito, ...Object.values(errors.polja)].join(" "),
              );
            })
            .finally(() => setPending(false));
        }}
      >
        {!place && (
          <Field label="Vrsta mjesta">
            <div className="equipment-choices">
              <Button
                type="button"
                variant="outline"
                aria-pressed={kind === "baza"}
                onClick={() => {
                  setKind("baza");
                  setExternal(false);
                  setSupplier("");
                }}
              >
                Baza / skladište
              </Button>
              <Button
                type="button"
                variant="outline"
                aria-pressed={kind === "radiona"}
                onClick={() => setKind("radiona")}
              >
                Radiona
              </Button>
            </div>
          </Field>
        )}
        <Field label="Naziv mjesta">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={200}
          />
        </Field>
        {duplicate && (
          <p className="equipment-warning" role="status">
            Mjesto s ovim nazivom već postoji. Provjeri naziv; spremanje je i
            dalje dopušteno.
          </p>
        )}
        {!place && kind === "radiona" && (
          <Field label="Čija radiona">
            <div className="equipment-choices">
              <Button
                type="button"
                variant="outline"
                aria-pressed={!external}
                onClick={() => {
                  setExternal(false);
                  setSupplier("");
                }}
              >
                Naša radiona
              </Button>
              <Button
                type="button"
                variant="outline"
                aria-pressed={external}
                onClick={() => setExternal(true)}
              >
                Vanjska radiona
              </Button>
            </div>
          </Field>
        )}
        {external && (
          <>
            <Feedback
              error={
                suppliers.error
                  ? "Popis dobavljača nije dostupan. Pokušaj ponovno."
                  : ""
              }
            />
            {suppliers.loading && <p role="status">Učitavanje dobavljača…</p>}
            {!suppliers.loading &&
              !suppliers.error &&
              suppliers.data?.length === 0 && (
                <p role="status">
                  Nema dobavljača. Vanjsku radionu najprije dodaj u Nabava →
                  Dobavljači.
                </p>
              )}
            {suppliers.error && (
              <Button
                type="button"
                variant="outline"
                onClick={suppliers.reload}
              >
                Pokušaj ponovno
              </Button>
            )}
            <Field label="Dobavljač">
              <select
                required
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
              >
                <option value="">Odaberi dobavljača</option>
                {suppliers.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.naziv}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}
        <Feedback error={error} />
        <Button
          type="submit"
          disabled={
            pending ||
            !name.trim() ||
            (external && (!supplier || !!suppliers.error))
          }
        >
          {pending ? "Spremam…" : place ? "Spremi naziv" : "Dodaj mjesto"}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={onClose}
        >
          Odustani
        </Button>
      </form>
    </EquipmentDetail>
  );
}
